/**
 * The destination catalogue list and its add/rename form (T651 M2 scaffolding,
 * filled by M4).
 *
 * ⭐ WHAT THIS SCREEN IS FOR, IN ONE LINE: the places on a customer's card are
 * DATA, not a deploy. Owen's two paper sheets became 29 rows here, and the
 * thirtieth is typed in rather than released.
 *
 * ⛔ ROUTING IS THE PARENT'S AND STAYS THERE. Admin.tsx owns the view union and
 * the loaders; AccountBook.tsx owns the tabs, the truncation notice and this
 * file's prop contract. Nothing here reopens either.
 *
 * ⛔ DEACTIVATE, NEVER DELETE (R38), AND AN INACTIVE ROW STAYS VISIBLE-BUT-
 * MARKED rather than dropping out. That is the lesson the closed-golf-course
 * list already paid for, in its own words: "an absent course and a
 * still-present unmarked one are indistinguishable to her". What `active = false`
 * MEANS is decided once, at core/data/destinations-repo.ts's
 * setDestinationActiveOn, and this file is its READER - a closed destination
 * leaves this list's default view. That reader is the point: this schema already
 * carries three flags nothing reads.
 *
 * ⚠️ AND THE ONE THIS SCREEN CANNOT SHOW: A PILOT HOME IS BOTH AN ACCOUNT AND A
 * DESTINATION. Each home is a place the OTHER home's residents travel to, so it
 * has a row on both tabs, with two ids and two lifecycles. Taking it out of use
 * HERE does not take it out of use on the accounts tab, or the other way round -
 * nothing links them, deliberately (see the repo's note for why a link would be
 * wrong). If a home closes outright, both rows have to be closed.
 *
 * ⛔ A RENAME IS SAFE HERE AND A MERGE IS NOT OFFERED. The name is a display
 * label on an opaque key, so correcting Owen's spelling of a hospital changes
 * what is rendered and nothing else - no price, no booking and no series
 * re-resolves. Renaming ONTO a name already on the list is refused rather than
 * folding two rows into one: this repo has no FK-repointing operation anywhere,
 * and the house answer to a duplicate is refuse-and-surface.
 * ⚠️ THE CASE THAT NEEDS THIS AND IS NOT A RENAME: four names on the captured
 * sheets may each turn out to be TWO different places at two different prices. A
 * split is 1 -> 2 and nothing guards it; the audited path is to take the wrong
 * one out of use and add the two, which this screen can already do.
 *
 * ⛔ EVERY WRITE CARRIES A REASON, AND IT IS NOT DECORATION. It is stored in the
 * v27 lifecycle journal beside who acted, inside the same transaction as the
 * change. S2 asked for it from the start and it was dissolved at M3 for one
 * reason - there was nowhere to put it - which is no longer true.
 *
 * ⛔ NO ROW CONTROL CARRIES THE DESTINATION'S NAME IN ITS ACCESSIBLE NAME, the
 * same two rules AccountList.tsx states: the feedback widget's click recorder
 * reads a control's accessible name into a stored trail and its own sanitiser
 * "catches emails and 7+ digit runs, never names"; and a row button called
 * `Edit <name>` would let somebody create a destination called "Continue" and
 * change what a Playwright accessible-name matcher resolves to on a different
 * screen. The row's identity is carried by the list context instead.
 *
 * ⚠️ S28 (a) - THE CLICK CHANNEL - IS MARKED HERE, AND ON THIS SCREEN THE
 * MARKERS ARE INERT TODAY. Every control below is `data-fb-opaque`, read by
 * feedback/clicks.ts; the blanket is deliberate because a uniform rule survives
 * edits better than one applied to the element somebody reasoned about. But do
 * not read the count as the strength: there is no <select> here (the one control
 * type whose textContent is every option concatenated), the buttons are labelled
 * with static copy, and an <input>'s textContent is empty so a text field falls
 * to the id branch. ⚠️ Note where the name actually sits - in the
 * <span class="op-name"> SIBLING of the buttons, which is not in
 * INTERACTIVE_SELECTOR, so a click on it resolves no control at all. If a row is
 * ever made clickable as a whole, THAT control is the one that needs the marker.
 * ⛔ S28 (b) - THE SCREENSHOT CHANNEL - IS A RECORDED RESIDUAL AND NOT A FIX,
 * carried forward from M3 unchanged rather than silently inherited.
 * `data-fb-opaque` is honoured ONLY by the click recorder; the capture path
 * blanks the VALUE of cloned inputs and reproduces rendered TEXT verbatim, so
 * the destination names below DO reach the JPEG on a build with the widget
 * enabled. What bounds it: the widget is build-gated (a code property, not a
 * deployment guarantee - the posture is chosen at container start from the
 * service's variables). What would close it is extending the capture filter
 * under src/client/feedback, which is generated from the copy under site/ - the
 * barrier is this milestone's file-ownership boundary plus `sync:feedback:check`,
 * not a cross-repo change.
 * ⛔ AND THE CAPTURE FILTER'S OWN MARKER ATTRIBUTE IS BARRED AS THE MITIGATION,
 * the same literal `AccountList.tsx` records: `scripts/check-killswitch.mjs`
 * greps the built client, and the served sourcemap embeds these comments, so
 * even naming the literal fails the gate.
 * ⚠️ THE CONSENT ITEM M3 RE-RAISED WIDENS AGAIN HERE, which is the part a
 * decision-maker needs: the open `PENDING - ALEX` ask for "an explicit nod that
 * kiosk screenshots may carry customer PII" was raised when the kiosk held
 * synthetic fixtures. M3 changed its subject to two named real customer
 * organisations; this milestone adds 29 named real HEALTHCARE FACILITIES those
 * customers' residents travel to. The list of places a named care home sends
 * residents is a different kind of fact from the home's own address, and it must
 * not be closed against the old subject.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ApiDestination } from '../../lib/actions.js';
import {
  createDestination,
  renameDestination,
  setDestinationActive,
  type DestinationResult,
} from '../../lib/destination-actions.js';
import { EmptyState } from '@ecmb/ui';
import { Banner } from '../../components/Banner.js';

export type DestinationListProps = {
  destinations: ApiDestination[];
  /**
   * True when an empty `destinations` might NOT mean "there are none" — the read
   * failed, or it has not resolved yet. Suppresses the empty-state sentence,
   * because AccountBook is saying the honest thing above instead.
   *
   * ⛔ ABSENT MEANS THE CALLER HAS **PROVEN** THIS LIST IS REALLY EMPTY. Any call
   * site rendering this list from a read that can fail must pass it. It is
   * optional only so the three shipped spec harnesses compile unchanged; a
   * second production call site that forgets it silently reinstates the defect
   * (T698), and `tsc` will not say a word. AccountBook.tsx owns this contract.
   */
  emptinessUnknown?: boolean;
  /** The house invalidation (Admin.tsx passes `load`). */
  onChanged: () => void;
};

/**
 * The name fold the duplicate pre-check uses, matching the one the server
 * compares with (core/data/accounts-repo.ts's normaliseAccountName, then a
 * lower-case comparison in SQL and now in a unique index).
 *
 * ⚠️ RE-DECLARED RATHER THAN IMPORTED, exactly as AccountList.tsx and
 * Operators.tsx re-declare theirs and for the same reason: the repo module pulls
 * in `pg` and would drag a node-only dependency into the browser bundle. Drift
 * can only ever cost the operator a round trip - the server's 409 stays
 * authoritative, so a fold that disagrees can never turn a refusal into an
 * accept.
 */
const foldName = (raw: string): string =>
  raw.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * ⭐ T1723 M3 (II-admin-F1). The id the refused reason field points its
 * `aria-describedby` at, and the id the refusal banner then carries. A module
 * constant, not a literal at two sites: the whole mechanism is that the two
 * spellings match. Same shape as `RateList.tsx`'s `STRIP_ERROR_ID`.
 */
const REASON_ERROR_ID = 'destination-reason-error';

export function DestinationList({
  destinations,
  emptinessUnknown = false,
  onChanged,
}: DestinationListProps) {
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  /** null = the form is adding; a number = the form is renaming that row. */
  const [editing, setEditing] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  /**
   * ⭐ T1723 M3 (K2) - WHICH ROW IS ASKING "ARE YOU SURE?". null = none.
   *
   * ⛔ THE DEFECT: `Take out of use` FIRED ON ONE CLICK, on every row, with no
   * confirm and no undo, against DESIGN.md §5.1 ("Destructive ... confirm +
   * undo"). The write is journalled and append-only, so there is nothing to undo
   * afterwards - which makes the confirm the only place the operator can change
   * her mind. The shape is Inbox.tsx's dismiss swap verbatim: the destructive
   * REPLACES itself with a Yes/Keep pair rather than opening a dialog.
   */
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  /**
   * ⭐ T1723 M3 (II-admin-F1, F10) - THE THREE FIELDS THE SCREEN HAS TO BE ABLE
   * TO PUT THE CARET IN.
   *
   * ⛔ THE FINDING: "Say why..." named a field the operator could not see, with
   * no `aria-invalid` and no `aria-describedby`; and `Rename` retitled a form
   * ~2000px below with no scroll and no focus, so the click looked inert.
   * ⛔ FOCUS, NOT `scrollIntoView` - `PriceList.tsx:327`'s standing ruling. Focus
   * scrolls as a side effect AND tells a screen-reader user where she now is.
   * ⚠️ TWO reason fields, and which one the refusal lands on depends on WHICH
   * ACT was refused: the confirm swap owns its own (K2), the Add form owns
   * `#dest-note`. `rowNoteRef` is null whenever no swap is open, so the pending
   * target is chosen at refusal time rather than assumed.
   */
  const noteRef = useRef<HTMLInputElement>(null);
  const rowNoteRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  /**
   * ⭐ T1723 M4 - THE ESCAPE HATCH GIVES THE CARET BACK (WCAG 2.4.3, and the
   * other half of the effect below).
   *
   * ⛔ THE FINDING, measured live: `Keep it` closed the swap and dropped focus
   * to `<body>`. Opening announced itself and abandoning did not, so a keyboard
   * operator who changed her mind lost her place in the list entirely and had
   * to tab from the top to reach the next row.
   * ⛔ A MAP KEYED BY `destinationId`, NOT ONE `useRef` - the at-rest button is
   * rendered once PER ROW, so a single ref would hold whichever row rendered
   * last rather than the row that asked. The key is the row's identity, which
   * is the same key the list itself uses.
   * ⚠️ `pendingRowFocus` is written ONLY by `Keep it`, the same explicit shape
   * `pendingFormFocus` uses above: `run`'s success path and `startAdd` /
   * `startEdit` also close the swap, and each of those already puts the caret
   * somewhere of its own (the notice banner, the form). A close-means-focus
   * rule would steal it back.
   */
  const rowToggleRefs = useRef(new Map<number, HTMLButtonElement>());
  const pendingRowFocus = useRef<number | null>(null);
  /**
   * ⛔⛔ THE CONFIRM SWAP OWNS ITS REASON, AND THE ADD FORM OWNS ITS OWN
   * (T1723 M3 security-review advisory, reproduced with a probe).
   *
   * THE DEFECT THIS SPLITS: K2 put a reason field in the confirm swap and bound
   * it to the screen's shared `note`, so the two <input>s live-synced. Closing
   * the swap's own transitions (the gate BLOCK) was not enough, because TWO
   * acts that never touch the swap still read `note` while a confirm is open:
   *   (a) `onSave` POSTed `{name: "Brand New Place", note: "<A's reason>"}`;
   *   (b) `Put back in use` on a DIFFERENT row POSTed
   *       `{destinationId: B, active: true, note: "<A's reason>"}`.
   * `registry_events` is INSERT-only (core/roles.sql), so both are PERMANENT
   * misattributions on a journal that reads as authoritative.
   *
   * ⭐ THE HOUSE PRECEDENT IS `PriceList.tsx:536` - A REASON IS BOUND TO ITS
   * SUBJECT. Two states, and every act reads the field it was typed into: the
   * destructive confirm reads `confirmNote`, and adding, renaming and putting a
   * place back in use read `note`. No act can spend a sentence written for
   * another one, because it cannot see it.
   * ⚠️ This is the "per-row reason input" the `run` note below argues against,
   * and the argument still holds: this is ONE more field, not one per row - at
   * most one confirm is open at a time.
   */
  const [confirmNote, setConfirmNote] = useState('');
  /**
   * WHICH reason field a refusal is about, so the red border and the
   * `aria-describedby` land on the box that is actually being asked for. A
   * boolean marked BOTH renderings, which was harmless while they held one
   * value and is a lie now that they hold two.
   */
  const [refusedField, setRefusedField] = useState<'form' | 'confirm' | null>(null);
  /** Where the next notice should put the caret; null keeps the banner. */
  const focusOnNotice = useRef<{ current: HTMLElement | null } | null>(null);
  /** Why focus is about to move into the FORM. Explicit, so a save's
   *  confirmation is never stolen back into the form (PriceList.tsx's rule). */
  const pendingFormFocus = useRef<'edit' | null>(null);
  // The double-submit latch, and it cannot be `busy`: `if (busy) return` reads a
  // value fixed at RENDER time and setBusy does not re-render before the next
  // click in the same task, so two fast taps both see false. A ref is written and
  // read synchronously. (Operators.tsx measured this; the shape is copied.)
  const inFlight = useRef(false);

  // Where the caret goes after an action. The control that was clicked is
  // `disabled` while the request is in flight, and the browser blurs a disabled
  // element, so focus drops to <body> on every action without this.
  useEffect(() => {
    if (notice === null) return;
    // ⭐ T1723 M3: a refusal that NAMES A FIELD lands on the field. The banner is
    // still `role="alert"`, so the sentence is announced either way; what
    // changes is that the caret, and therefore the viewport, ends up at the box.
    const field = focusOnNotice.current;
    focusOnNotice.current = null;
    if (field != null) { field.current?.focus(); return; }
    noticeRef.current?.focus();
  }, [notice]);

  /** II-admin-F10: `Rename` moves the caret into the form it just retitled. */
  useEffect(() => {
    const why = pendingFormFocus.current;
    pendingFormFocus.current = null;
    if (why === 'edit') nameRef.current?.focus();
  }, [editing]);

  /**
   * ⭐ WCAG 4.1.3 (ux-review) - OPENING THE CONFIRM ANNOUNCES ITSELF.
   *
   * ⛔ THE FINDING: tapping `Take out of use` replaced the row's two buttons
   * with a field and two new ones, and `document.activeElement` stayed on
   * `<body>` - the control that was clicked had just been unmounted. So a
   * keyboard or screen-reader operator was told nothing at all had happened,
   * on the one interaction whose whole purpose is to ask her a question.
   * ⛔ FOCUS, NOT AN ARIA-LIVE ANNOUNCEMENT: the swap is not a status, it is a
   * QUESTION with a required answer, and the answer's box is where she has to
   * be. Focus scrolls it into view as a side effect and names it as she lands.
   * Same mechanism `PriceList.tsx:327` rules for the sibling case and the same
   * shape the refusal path above uses.
   * ⚠️ Keyed on `confirmingId`, so it fires on OPEN, on a MOVE from one row to
   * another, and on CLOSE - where T1723 M4 added the symmetric half. The close
   * branch is gated on `pendingRowFocus` rather than on "confirmingId became
   * null", because only `Keep it` is an ABANDONMENT: a completed write puts the
   * caret on its notice and `Rename` puts it in the form, and both of those
   * also close the swap.
   */
  useEffect(() => {
    if (confirmingId === null) {
      const rowId = pendingRowFocus.current;
      pendingRowFocus.current = null;
      if (rowId !== null) rowToggleRefs.current.get(rowId)?.focus();
      return;
    }
    rowNoteRef.current?.focus();
  }, [confirmingId]);

  const run = async (action: () => Promise<DestinationResult>, okText: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const result = await action();
      if (!result.ok) {
        setNotice({ kind: 'error', text: result.message });
        return;
      }
      setNotice({ kind: 'ok', text: okText });
      // The house invalidation: refetch everything the screen shows, so the
      // price tab cannot keep naming a destination this tab just renamed.
      onChanged();
    } finally {
      inFlight.current = false;
      setBusy(false);
      // ⛔⛔ THE ONE RESET, AND IT IS HERE BECAUSE THIS IS THE ONE PLACE ALL FOUR
      // ACTIONS PASS THROUGH (T651 M4 output gate, shard B - a CONFIRMED defect,
      // reproduced with a probe).
      //
      // THE DEFECT: `note` is ONE piece of screen state shared by the form AND
      // the two row buttons, and only three of the four exits reset it -
      // startAdd, startEdit and onSave's success branch. The row toggles call
      // their action directly and never reset. So: type a reason, take row A out
      // of use, then click row B WITHOUT RETYPING, and row B's write carried row
      // A's reason verbatim.
      //
      // ⛔ WHY THAT IS SERIOUS RATHER THAN UNTIDY: registry_events is APPEND-ONLY
      // WITH NO ERASURE PATH (core/roles.sql REVOKEs UPDATE and DELETE), so a
      // misattributed reason is PERMANENT - and it defeats the exact promise the
      // copy below makes, that the reason is kept against THIS change. An audit
      // trail that confidently attributes the wrong reason to the wrong subject
      // is worse than none, because it reads as authoritative. It is also the
      // highest-traffic path: taking a row out of use is one click.
      //
      // ⭐ THE RULE CHOSEN, AND IT IS STRONGER THAN "CLEAR ON SUCCESS": A REASON
      // IS CONSUMED BY EXACTLY ONE ATTEMPT, whatever the outcome. `finally`, not
      // the success branch. Clear-on-success would leave the SAME defect in a
      // narrower state - a failed attempt would still let row A's reason reach
      // row B - and "measured in four of five states" is not measured. The cost
      // is stated rather than hidden: after a server error the operator retypes
      // her sentence. That is seconds at a keyboard, against a permanent wrong
      // attribution on a table nothing can correct.
      //
      // ⛔ AND NOT A PER-ROW REASON INPUT, which was the alternative. The sharing
      // is deliberate and the label says so ("needed for adding, ... and taking
      // ... out of use"); one field for four acts is a simplicity decision on a
      // screen whose hard constraint is that the operators bounced off a tool
      // that was "too many buttons". A per-row field multiplies it by the row
      // count. The bug was never the sharing - it was an exit that did not
      // reset - so the fix belongs at the shared exit, where a FIFTH action
      // added later inherits it for free.
      setNote('');
      // ⭐ AND THE CONFIRM'S OWN REASON WITH IT. Both, not "whichever the act
      // used": an attempt is a state change on the whole screen, and a primed
      // confirm still holding a sentence after some other row was written is
      // exactly the leak class this screen has now produced twice. Clearing the
      // one it did not use costs a retype and can never misattribute.
      setConfirmNote('');
      setRefusedField(null);
      // ⭐ T1723 M3 (K2) - AND THE CONFIRM IS CONSUMED WITH IT, for the same
      // reason and at the same exit. An attempt spends BOTH: leaving the swap
      // open after a server error would leave a primed destructive on a row
      // whose reason has just been cleared, so the next tap on `Yes` is refused
      // for a reason that reads like the server's. One rule, one place, and a
      // fifth action added later inherits it exactly as it inherits the reason.
      // ⚠️ A REFUSAL IS NOT AN ATTEMPT: `missingReason()` returns before `run`
      // is ever called, so the swap stays open around the field it is asking
      // her to fill in.
      setConfirmingId(null);
    }
  };

  /**
   * ⛔⛔ EVERY TRANSITION OF THE CONFIRM SWAP STARTS THE REASON EMPTY, AND THIS
   * IS THE ONE PLACE THAT SAYS SO (T1723 M3 output gate, a CONFIRMED defect
   * reproduced with a probe in all three of its shapes).
   *
   * THE DEFECT: `note` is ONE piece of screen state shared by the Add form and
   * the swap, and the only thing that cleared it was `run`'s `finally` - which
   * fires on a real write ATTEMPT and nothing else. `Keep it` and opening the
   * confirm on a DIFFERENT row both bypass `run` entirely. So: open row A's
   * confirm, type a reason, open row B's confirm, and B's field was pre-filled
   * with A's sentence; tapping `Yes` there POSTed
   * `{destinationId: B, active: false, note: "<A's reason>"}`.
   *
   * ⛔ WHY THAT IS SERIOUS RATHER THAN UNTIDY, and it is the same argument
   * `run`'s finally already makes one screen down: `registry_events` is
   * APPEND-ONLY WITH NO ERASURE PATH (core/roles.sql REVOKEs UPDATE and
   * DELETE), so a misattributed reason is PERMANENT - and it defeats the exact
   * promise this screen's own copy makes, that the reason is kept against THIS
   * change. K2 made it WORSE than the shape the T651 gate fixed, not better:
   * the field now sits in the row, so the stale sentence is pre-filled under
   * the operator's eyes and reads as something she typed for THIS place.
   *
   * ⛔ THE RULE, AND IT IS THE SAME ONE `run`'s finally states: A REASON IS
   * CONSUMED BY EXACTLY ONE ACT. Opening a confirm STARTS an act, so it starts
   * empty; `Keep it` ABANDONS one, so it leaves nothing behind. Both go through
   * here rather than resetting at two call sites, because two copies of a reset
   * is precisely how one of them gets forgotten - which is how this defect
   * happened in the first place.
   * ⚠️ IT CLEARS THE ADD FORM'S FIELD TOO, deliberately: it is the same value,
   * and a reason typed for a rename does not belong to a deactivation either.
   * `startEdit` and `startAdd` already take that position.
   * ⛔ NOT called from `run`'s `finally`: that would null the notice `run` has
   * just set, which is the operator's only report of what happened.
   */
  const setConfirming = (destinationId: number | null) => {
    setConfirmingId(destinationId);
    setConfirmNote('');
    setRefusedField(null);
    setNotice(null);
  };

  const startAdd = () => {
    setEditing(null);
    setName('');
    setNote('');
    // The one place a swap is closed outside `run`: it takes the notice, the
    // refusal and the swap's own reason with it.
    setConfirming(null);
  };

  const startEdit = (destination: ApiDestination) => {
    // II-admin-F10: the form is screens below the row that was clicked.
    pendingFormFocus.current = 'edit';
    setEditing(destination.destinationId);
    setName(destination.name);
    setNote('');
    // An open confirm on ANOTHER row would leave two half-finished acts on
    // screen at once. Its reason no longer shares state with this form, so it
    // is discarded here rather than inherited.
    setConfirming(null);
  };

  /**
   * One refusal for every action on this screen, so the add form, the rename
   * form and the two row buttons cannot disagree about what is required.
   *
   * ⛔ THE REASON IS PASSED IN, NEVER READ FROM STATE, and `which` names the box
   * it came out of. That is the whole mechanism of the fix above: a gate that
   * looked up "the reason" was a gate that could approve one act on another
   * act's sentence. The caller holds the binding; this only decides whether the
   * sentence it was given is good enough.
   */
  const missingReason = (which: 'form' | 'confirm', reason: string): boolean => {
    if (reason.trim() !== '') {
      setRefusedField(null);
      return false;
    }
    setRefusedField(which);
    focusOnNotice.current = which === 'confirm' ? rowNoteRef : noteRef;
    setNotice({
      kind: 'error',
      text: 'Say why, in a few words. It is kept with your name against this change.',
    });
    return true;
  };

  const onSave = () => {
    const typed = name.trim();
    if (typed === '') {
      setNotice({ kind: 'error', text: 'The destination needs a name before it can be saved.' });
      return;
    }
    // Answered before the round trip, from the list already loaded. The server's
    // 409 stays the authority for the race, and since migration v27 so does a
    // unique index; this is the sentence that names the fix.
    // ⚠️ AHEAD OF THE REASON CHECK ON PURPOSE: a name that cannot be saved at all
    // is worth saying before asking the operator to justify saving it.
    const clash = destinations.some(
      (d) => d.destinationId !== editing && foldName(d.name) === foldName(typed),
    );
    if (clash) {
      setNotice({
        kind: 'error',
        text: 'That destination is already on the list. Use a name that tells the two apart, such as adding the town.',
      });
      return;
    }
    // The Add form's own box, never whatever a confirm swap is holding.
    if (missingReason('form', note)) return;
    const target = editing;
    const reason = note.trim();
    void run(async () => {
      const result = target == null
        ? await createDestination(typed, reason)
        : await renameDestination(target, typed, reason);
      if (result.ok) {
        setEditing(null);
        setName('');
      }
      return result;
    }, target == null ? 'Destination added.' : 'Destination saved.');
  };

  const closedCount = destinations.filter((d) => !d.active).length;
  // ⭐ THE READER OF `active`. A closed destination is out of the default view,
  // which is what "hidden from new work" means on the one screen that has to be
  // able to put it back.
  const visible = showClosed ? destinations : destinations.filter((d) => d.active);

  return (
    <>
      {notice && (
        <Banner
          kind={notice.kind}
          id={refusedField !== null ? REASON_ERROR_ID : undefined}
          elementRef={noticeRef}
          tabIndex={-1}
        >
          {notice.text}
        </Banner>
      )}

      {/* ⛔ THE SENTENCE IS ONLY SAFE WHEN THE EMPTINESS IS KNOWN. Both arms
          below are claims about what IS on the list, and a failed or unresolved
          read cannot support either: "No destinations on the list yet." over a
          catalogue of 29 rows the server never answered for is the T698 defect.
          AccountBook carries the honest line in that state. */}
      {/* ⭐⭐ T1749 M7 rework 1 — the library's `EmptyState` (§7.1), the same
          move its sibling screen's account list took. The two arms are unchanged:
          which is true is this screen's knowledge. */}
      {visible.length === 0 && !emptinessUnknown && (
        <EmptyState
          /* ⛔⛔ T1749 M9 rework 2 — NO TONE ON EITHER ARM, for the reason
             `AccountList.tsx` states beside its own: neutral is the default now,
             and neither "every destination is out of use" (a filter hiding rows)
             nor "none on the list yet" (setup nobody has done) is good news. The
             WORDS still tell the two apart; the paint no longer does. */
          headline={destinations.length === 0
            ? 'No destinations on the list yet. Add the first one in the form below.'
            : 'Every destination is out of use. Tick the box below to see them.'}
        />
      )}

      <ul class="op-list op-list--destinations" role="list">
        {visible.map((d) => {
          /**
           * The write both entry points perform, with THE REASON PASSED IN.
           *
           * ⛔ IT IS A PARAMETER RATHER THAN A LOOKUP, and that is the fix
           * rather than a style choice. While this read `note` off the screen,
           * `Put back in use` on row B spent whatever row A's open confirm was
           * holding and wrote it into an INSERT-only journal against B. The
           * caller is the only thing that knows which box the sentence came out
           * of, so the caller supplies it (`PriceList.tsx:536`: a reason is
           * bound to its subject).
           */
          const toggle = (which: 'form' | 'confirm', reason: string) => {
            if (missingReason(which, reason)) return;
            void run(
              () => setDestinationActive(d.destinationId, !d.active, reason.trim()),
              d.active
                ? 'That destination is no longer offered for new bookings. Everything already booked keeps it.'
                : 'That destination can be used again.',
            );
          };
          return (
          <li key={d.destinationId} class="op-row">
            <span class="op-name">{d.name}</span>
            {/* The flag column is always present so the two action columns stay
                columns: a conditional chip would slide `Rename` left on every
                in-use row and the buttons would never line up. */}
            <span class="op-row-flag">
              {!d.active && <span class="chip chip--quiet">Not in use</span>}
            </span>
            {confirmingId === d.destinationId ? (
              /* ⭐ THE CONFIRM SWAP (K2), Inbox.tsx:246-263's shape, plus the one
                 thing this screen needs that Inbox does not: THE REASON LIVES
                 HERE NOW. Every write on this screen carries a reason into the
                 append-only journal, and the field used to sit ~2000px below in
                 the Add form - so the destructive asked for something the
                 operator could not see. The destructive owns its own field.
                 ⛔ AND IT IS ITS OWN STATE (`confirmNote`), NOT the Add form's
                 `note`. K2 shared them and produced the same leak three times in
                 two review rounds: a reason typed for row A reached row B's
                 confirm pre-filled (the output gate), and then reached BOTH the
                 Add form's save and another row's `Put back in use` (the
                 security review), each POSTing it into an INSERT-only journal
                 against the wrong subject. Two states, and every act reads the
                 box it was typed into. `PriceList.tsx:536` is the precedent. */
              <span class="op-row-confirm">
                {/* A2-pricelists-1, this milestone's own rule, applied to the
                    field this milestone added: a VISIBLE label, not a
                    placeholder plus an aria-label. A placeholder disappears the
                    moment she types, which on a REQUIRED field is the worst
                    moment to lose it; and the `aria-label` is dropped rather
                    than kept beside the label, because two names for one
                    control is how the spoken name and the visible text drift
                    apart (WCAG 2.5.3). Every `getByLabelText` / `getByLabel`
                    query still resolves, through the `for` now instead.
                    ⚠️ The id carries the row so two open swaps could never
                    collide on it; only one is ever open today. */}
                <label class="field-label" for={`dest-out-note-${String(d.destinationId)}`}>
                  Why is it going out of use?
                </label>
                {/* ⛔ NO `placeholder` ON THIS BOX (II-admin-F22, T1723 M4). It
                    repeated the visible label directly above it WORD FOR WORD,
                    so the row asked the same question twice on one line and the
                    second copy vanished the moment she typed. The label is the
                    accessible name and it SURVIVES typing, which was
                    A2-pricelists-1's whole point. A placeholder that earned its
                    place here would have to say something the label does not -
                    an EXAMPLE - and "why is this place closing" has no
                    representative one. */}
                <input
                  id={`dest-out-note-${String(d.destinationId)}`}
                  class="login-input op-row-why"
                  type="text"
                  ref={rowNoteRef}
                  data-fb-opaque
                  autocomplete="off"
                  {...(refusedField === 'confirm' ? { 'aria-invalid': 'true', 'aria-describedby': REASON_ERROR_ID } : {})}
                  value={confirmNote}
                  onInput={(e) => { setRefusedField(null); setConfirmNote((e.target as HTMLInputElement).value); }}
                />
                <button
                  type="button"
                  class="btn btn--quiet btn--destructive"
                  data-fb-opaque
                  disabled={busy}
                  onClick={() => toggle('confirm', confirmNote)}
                >
                  Yes, take it out of use
                </button>
                <button
                  type="button"
                  class="btn btn--quiet"
                  data-fb-opaque
                  disabled={busy}
                  onClick={() => {
                    // ⭐ The one writer of `pendingRowFocus`: abandoning the
                    // question hands the caret back to the control that asked
                    // it, which is about to re-mount in this very row.
                    pendingRowFocus.current = d.destinationId;
                    setConfirming(null);
                  }}
                >
                  Keep it
                </button>
              </span>
            ) : (
              <>
                <button
                  type="button"
                  class="btn btn--quiet"
                  data-fb-opaque
                  disabled={busy}
                  onClick={() => startEdit(d)}
                >
                  Rename
                </button>
                <button
                  type="button"
                  class={d.active ? 'btn btn--quiet btn--destructive' : 'btn btn--quiet'}
                  data-fb-opaque
                  ref={(el) => {
                    // Registered per ROW so `Keep it` can hand the caret back
                    // to the button that opened the swap. Deleted on unmount so
                    // the map cannot hold a detached node from a row the
                    // refetch dropped.
                    if (el === null) rowToggleRefs.current.delete(d.destinationId);
                    else rowToggleRefs.current.set(d.destinationId, el);
                  }}
                  disabled={busy}
                  onClick={() => {
                    // ⛔ ONLY THE DESTRUCTIVE DIRECTION ASKS. Putting a place
                    // back in use is additive and reversible by the same
                    // control, so a confirm there would be ceremony (§5.1 asks
                    // for confirm + undo on the DESTRUCTIVE level only).
                    if (d.active) { setConfirming(d.destinationId); return; }
                    // ⛔ THE ADD FORM'S BOX, and it is named rather than looked
                    // up: this direction has no field of its own (the copy under
                    // `#dest-note` says it is needed for putting one back in
                    // use), so it must read THAT one and never a confirm swap
                    // open on some other row.
                    toggle('form', note);
                  }}
                >
                  {d.active ? 'Take out of use' : 'Put back in use'}
                </button>
              </>
            )}
          </li>
          );
        })}
      </ul>

      {closedCount > 0 && (
        <p class="helper-note">
          <label class="checkbox-inline">
            <input
              type="checkbox"
              data-fb-opaque
              checked={showClosed}
              onChange={(e) => setShowClosed((e.target as HTMLInputElement).checked)}
            />
            Show destinations not in use
          </label>
        </p>
      )}

      {/* ⭐ A REAL SEPARATOR ELEMENT (K2, DESIGN.md §5.1 "destructive never
          adjacent to primary"). `ul.op-list`'s nextElementSibling was
          `div.op-add`, whose first control is the GOLD `Add destination` - so
          the list's last row put a red `Take out of use` directly above the
          screen's one primary, with nothing between them. The <h3> inside
          `.op-add` separates nothing (it is inside the block, not between the
          two), and the "Show destinations not in use" paragraph renders only
          when `closedCount > 0`. `.section-rule` is the class Done.tsx:467
          already uses for exactly this job. */}
      <hr class="section-rule" />
      <div class="op-add">
        <h3 class="screen-sub">{editing == null ? 'Add a destination' : 'Rename this destination'}</h3>

        <label class="field-label" for="dest-name">Destination name</label>
        <input
          id="dest-name"
          class="login-input"
          type="text"
          ref={nameRef}
          data-fb-opaque
          autocomplete="off"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />

        {/* ⛔ THE REASON FOR THE THREE ACTS THIS FORM OWNS: adding, renaming and
            putting a place back in use. The destructive has its own box inside
            its confirm swap and its own state (`confirmNote`), because a
            refusal that named THIS field from ~2000px away was unusable and
            because a shared value let one act spend another's sentence. One
            rule (`missingReason`), one exit (`run`'s finally), two subjects. */}
        <label class="field-label" for="dest-note">Why</label>
        <input
          id="dest-note"
          class="login-input"
          type="text"
          ref={noteRef}
          data-fb-opaque
          autocomplete="off"
          {...(refusedField === 'form' ? { 'aria-invalid': 'true', 'aria-describedby': REASON_ERROR_ID } : {})}
          value={note}
          onInput={(e) => { setRefusedField(null); setNote((e.target as HTMLInputElement).value); }}
        />
        {/* ⭐ T1749 M7 rework 1 (Q139) — `.field-hint`, not `.helper-note`. It sits
            under a field and says what shape the answer takes, which is what a
            hint is; spelled `.helper-note` it rendered at BODY size with no top
            margin beside caption-sized siblings, and the Q139 census, which counts
            `.field-hint`, could not see it at all. Class only: these screens'
            hint WORDS are graded at M9, when they adopt `FormField`. */}
        <p class="field-hint">
          A few words, kept with your name against this change. It is needed for adding, renaming
          and putting a destination back in use. Taking one out of use asks for it on the row.
        </p>

        <div class="admin-add-actions">
          <button
            type="button"
            class="btn btn--primary"
            data-fb-opaque
            disabled={busy}
            onClick={onSave}
          >
            {editing == null ? 'Add destination' : 'Save this destination'}
          </button>
          {editing != null && (
            <button
              type="button"
              class="btn btn--quiet"
              data-fb-opaque
              disabled={busy}
              onClick={startAdd}
            >
              Stop editing
            </button>
          )}
        </div>
      </div>
    </>
  );
}
